/**
 * React hook wrapping all proposal API calls through the Finance API client.
 */
import { useCallback, useMemo } from "react";
import useFinanceApiClient from "../../../hooks/useFinanceApiClient";
import type { Proposal, ProposalModel, DiamondClubMember } from "./types";

export interface Salesperson {
  id: string;
  display_name: string;
  email: string;
  phone: string | null;
  title: string | null;
}

interface ProposalApi {
  list: (status?: string) => Promise<Proposal[]>;
  get: (id: string) => Promise<Proposal>;
  create: (model: ProposalModel) => Promise<Proposal>;
  update: (id: string, model: ProposalModel) => Promise<Proposal>;
  regenSection: (id: string, sectionId: string, instruction?: string) => Promise<{ proposal: Proposal; text: string }>;
  preview: (id: string) => Promise<{ proposal: Proposal; preview_url: string }>;
  submit: (id: string) => Promise<Proposal>;
  approve: (id: string) => Promise<Proposal>;
  requestChanges: (id: string, reason: string) => Promise<Proposal>;
  download: (id: string, artifact: string) => Promise<string>;
  previewPdfBlobUrl: (id: string) => Promise<string>;
  listSalespersons: () => Promise<Salesperson[]>;
  listDiamondClub: () => Promise<DiamondClubMember[]>;
}

export function useProposalApi(): ProposalApi {
  const api = useFinanceApiClient();

  const list = useCallback(
    async (status?: string): Promise<Proposal[]> => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : "";
      const data = await api.get<{ proposals: Proposal[] }>(`/api/proposals${qs}`);
      return data.proposals;
    },
    [api],
  );

  const get = useCallback(
    async (id: string): Promise<Proposal> => {
      const data = await api.get<{ proposal: Proposal }>(`/api/proposals/${id}`);
      return data.proposal;
    },
    [api],
  );

  const create = useCallback(
    async (model: ProposalModel): Promise<Proposal> => {
      const data = await api.post<{ proposal: Proposal }>("/api/proposals", { model });
      return data.proposal;
    },
    [api],
  );

  const update = useCallback(
    async (id: string, model: ProposalModel): Promise<Proposal> => {
      const data = await api.put<{ proposal: Proposal }>(`/api/proposals/${id}`, { model });
      return data.proposal;
    },
    [api],
  );

  const regenSection = useCallback(
    async (id: string, sectionId: string, instruction?: string) => {
      const data = await api.post<{ proposal: Proposal; text: string }>(
        `/api/proposals/${id}/regen-section`,
        { section_id: sectionId, user_instruction: instruction || "" },
      );
      return data;
    },
    [api],
  );

  const preview = useCallback(
    async (id: string) => {
      const data = await api.post<{ proposal: Proposal; preview_url: string }>(
        `/api/proposals/${id}/preview`,
      );
      return data;
    },
    [api],
  );

  const submit = useCallback(
    async (id: string): Promise<Proposal> => {
      const data = await api.post<{ proposal: Proposal }>(`/api/proposals/${id}/submit`);
      return data.proposal;
    },
    [api],
  );

  const approve = useCallback(
    async (id: string): Promise<Proposal> => {
      const data = await api.post<{ proposal: Proposal }>(`/api/proposals/${id}/approve`);
      return data.proposal;
    },
    [api],
  );

  const requestChanges = useCallback(
    async (id: string, reason: string): Promise<Proposal> => {
      const data = await api.post<{ proposal: Proposal }>(
        `/api/proposals/${id}/request-changes`,
        { reason },
      );
      return data.proposal;
    },
    [api],
  );

  const download = useCallback(
    async (id: string, artifact: string): Promise<string> => {
      const data = await api.get<{ download_url: string }>(
        `/api/proposals/${id}/download/${artifact}`,
      );
      return data.download_url;
    },
    [api],
  );

  const previewPdfBlobUrl = useCallback(
    async (id: string): Promise<string> => {
      const allHeaders = await api.getAuthHeaders();
      const base = api.getBaseUrl();
      const { "Content-Type": _, ...headers } = allHeaders as Record<string, string>;
      const resp = await fetch(`${base}/api/proposals/${id}/preview-pdf`, { headers });
      if (!resp.ok) throw new Error(`PDF proxy failed: ${resp.status}`);
      const blob = await resp.blob();
      return URL.createObjectURL(blob);
    },
    [api],
  );

  const listSalespersons = useCallback(
    async (): Promise<Salesperson[]> => {
      const data = await api.get<{ salespersons: Salesperson[] }>("/api/proposals/salespersons");
      return data.salespersons;
    },
    [api],
  );

  const listDiamondClub = useCallback(
    async (): Promise<DiamondClubMember[]> => {
      const data = await api.get<{ diamond_club: DiamondClubMember[] }>("/api/proposals/diamond-club");
      return data.diamond_club;
    },
    [api],
  );

  return useMemo(
    () => ({ list, get, create, update, regenSection, preview, submit, approve, requestChanges, download, previewPdfBlobUrl, listSalespersons, listDiamondClub }),
    [list, get, create, update, regenSection, preview, submit, approve, requestChanges, download, previewPdfBlobUrl, listSalespersons, listDiamondClub],
  );
}
